// ============================================================
// Profileur : réponses du module « profiler » du satellite
// ============================================================
// Miroir de ProfilerViews (BE-Panel-Games). Toutes les dates sont des millisecondes depuis 1970 :
// la page les affiche à l'heure locale du navigateur. Les durées sont en millisecondes, les tailles
// en octets, les parts de 0 à 1.
// ============================================================

/** Un monde du dernier instantané (une minute de mesures). */
export interface LevelSummary {
  level: string;
  ticks: number;
  coveredSeconds: number;
  ticksPerSecond: number;
  averageTickMs: number;
  maxTickMs: number;
  /** Borne haute de la case de l'histogramme qui contient le centile ; null avec des ticks : 5 000 ms ou plus. */
  percentile95TickMs: number | null;
  percentile99TickMs: number | null;
  averageCpuMs: number | null;
  /** Octets alloués par tick ; null sans tick fini. */
  bytesPerTick: number | null;
  gcPauseMs: number;
  lagMs: number;
  /** Ticks de la minute au-dessus du seuil (200 ms par défaut), signalés ou non. */
  spikes: number;
  tickInProgress: boolean;
  tickInProgressMs: number;
  topStage: string | null;
  topStageShare: number | null;
  /**
   * Le profileur a cessé de mesurer ce monde après une erreur à lui (écrite une fois au journal du
   * serveur). Le monde tourne toujours ; ses autres chiffres datent d'avant. Absent d'un satellite
   * plus ancien : faux.
   */
  measuresStopped?: boolean;
}

export interface ProcessSummary {
  hasThreads: boolean;
  cpuCores: number;
  tickThreadsCpuCores: number;
  threadCount: number;
}

/** Dernier pic (« spike ») ou tick en cours (« stall ») d'un serveur. */
export interface EventSummary {
  kind: 'spike' | 'stall';
  t: number;
  level: string | null;
  ms: number;
  cause: string | null;
  stage: string | null;
}

export interface ServerSummary {
  /** Clé du serveur dans les adresses : identifiant Pterodactyl, sinon nom en minuscules. */
  key: string;
  name: string;
  /** Identifiant Pterodactyl (UUID) annoncé par le serveur ; null hors Pterodactyl. */
  id: string | null;
  model: string | null;
  group: string | null;
  engine: string | null;
  startedAt: number | null;
  firstSeenAt: number;
  lastSeenAt: number;
  lastSnapshotAt: number | null;
  /** Profileur allumé d'après le dernier instantané ; null si aucun instantané n'est arrivé. */
  enabled: boolean | null;
  paused: boolean;
  clockSkewMs: number | null;
  levels: LevelSummary[];
  process: ProcessSummary | null;
  /** Ticks trop longs de la dernière heure, hors les premières minutes après le démarrage du serveur. */
  spikesLastHour: number;
  /** Plus long tick de la dernière heure, hors ces mêmes minutes ; null sans mesure. */
  maxTickMsLastHour: number | null;
  lastEvent: EventSummary | null;
}

export interface ServerList {
  /** Horloge du satellite : « sans nouvelles depuis » se mesure avec elle. */
  now: number;
  pointsRetentionDays: number;
  eventsRetentionDays: number;
  /**
   * Premières secondes d'un serveur que le satellite écarte des compteurs de l'heure : la page ne juge
   * pas non plus un serveur pendant ce temps. Absent d'un satellite plus ancien : 300.
   */
  startupGraceSeconds?: number;
  servers: ServerSummary[];
}

/** Étape ou sous-étape d'un tick, telle que le moteur la nomme (Entities, ChunkTick/loaders...). */
export interface StageSnapshot {
  name: string;
  parent: string | null;
  totalMs: number;
  share: number;
  allocatedBytes: number;
  calls: number;
}

export interface LevelSnapshot {
  level: string;
  coveredSeconds: number;
  ticks: number;
  ticksPerSecond: number;
  averageTickMs: number;
  maxTickMs: number;
  percentile95TickMs: number | null;
  percentile99TickMs: number | null;
  averageCpuMs: number | null;
  allocatedBytes: number;
  gcPauseMs: number;
  gen0Collections: number;
  gen1Collections: number;
  gen2Collections: number;
  lagMs: number;
  spikes: number;
  stages: StageSnapshot[];
  subStages: StageSnapshot[];
  idle: StageSnapshot | null;
  histogram: { upperBoundMs: number | null; ticks: number }[];
  asyncTicks: number;
  averageAsyncTickMs: number;
  asyncAllocatedBytes: number;
  tickInProgress: boolean;
  tickInProgressMs: number;
}

export interface ThreadSnapshot {
  threadId: number;
  name: string;
  /** Part d'UN cœur, en pourcentage (100 = un cœur occupé en continu). */
  cpuPercent: number;
  userPercent: number;
  systemPercent: number;
  isTickThread: boolean;
}

export interface ProcessSnapshot {
  hasThreads: boolean;
  sampledAtUtc: number | null;
  sampleSeconds: number;
  cpuCores: number;
  tickThreadsCpuCores: number;
  threadCount: number;
  threads: ThreadSnapshot[];
}

export interface LatestSnapshot {
  enabled: boolean;
  paused: boolean;
  takenAtUtc: number | null;
  windowSeconds: number;
  levels: LevelSnapshot[];
  process: ProcessSnapshot | null;
}

export interface ServerDetail {
  now: number;
  /** Durée de garde de la courbe : borne la plus longue période proposée. Absent d'un satellite plus ancien : 7. */
  pointsRetentionDays?: number;
  /** Cf ServerList.startupGraceSeconds. */
  startupGraceSeconds?: number;
  server: ServerSummary;
  /** Dernier instantané complet ; null si le profileur est éteint ou si aucun n'est arrivé. */
  latest: LatestSnapshot | null;
}

/** Un point de la courbe : les instantanés tombés dans [t, t + stepSeconds[. */
export interface SeriesPoint {
  t: number;
  ticks: number;
  tps: number | null;
  /** null : aucun tick fini sur l'intervalle (monde gelé), jamais zéro. */
  avgMs: number | null;
  maxMs: number | null;
  p95Ms: number | null;
  /** Le centile est dans la dernière case de l'histogramme : 5 000 ms ou plus. */
  p95Open: boolean;
  p99Ms: number | null;
  p99Open: boolean;
  cpuMs: number | null;
  bytesPerTick: number | null;
  gcMs: number;
  lagMs: number;
  spikes: number;
  inProgressMs: number;
}

export interface StageTotal {
  name: string;
  parent: string | null;
  totalMs: number;
  share: number;
  msPerTick: number;
  allocatedBytes: number;
  bytesPerTick: number | null;
}

export interface SeriesTotals {
  ticks: number;
  tickTimeMs: number;
  avgMs: number | null;
  maxMs: number | null;
  tps: number | null;
  bytesPerTick: number | null;
  gcMs: number;
  spikes: number;
  /** Instantanés reçus sur la période. */
  minutes: number;
}

export interface Series {
  now: number;
  key: string;
  level: string | null;
  levels: string[];
  hours: number;
  stepSeconds: number;
  from: number;
  to: number;
  points: SeriesPoint[];
  stages: StageTotal[];
  subStages: StageTotal[];
  totals: SeriesTotals;
}

/** Un pic (« spike ») ou un tick en cours (« stall »). Les champs de l'autre sorte sont null. */
export interface ProfilerEvent {
  id: string;
  kind: 'spike' | 'stall';
  t: number;
  receivedAt: number;
  level: string | null;
  tick: number | null;
  wallMs: number | null;
  elapsedMs: number | null;
  cpuMs: number | null;
  allocatedBytes: number | null;
  gcPauseMs: number | null;
  gen0Collections: number | null;
  gen1Collections: number | null;
  gen2Collections: number | null;
  players: number | null;
  entities: number | null;
  chunks: number | null;
  lagMs: number | null;
  overDuration: boolean | null;
  overAllocation: boolean | null;
  stages: StageSnapshot[] | null;
  subStages: StageSnapshot[] | null;
  /** Indice tel que le moteur l'écrit (anglais). */
  hint: string | null;
  /** Code de cause déduit par le satellite : waiting, computing, busyLoop, gc, allocation, unknown. */
  cause: string | null;
  stage: string | null;
  markers: string | null;
}

export interface EventList {
  now: number;
  events: ProfilerEvent[];
}

/** Périodes proposées, en heures (celles que le satellite sait regrouper). */
export const PERIODS = [1, 6, 24, 168] as const;
export type PeriodHours = (typeof PERIODS)[number];
